
import React from "react";
import SectionContainer from "@/marketing/components/ui/section-container";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { trackClick } from "@/shared/utils/analytics";

const StorySection = () => {
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();

  // Summarized versions of the story paragraphs for the homepage
  const summaries = [
    t('story.summary1', 'We studied the world\'s best traders and discovered that consistent profits come from disciplined execution—not complex platforms or market timing.'),
    t('story.summary2', 'Every investor—retail to institutional—deserves a platform that speaks natural language and adapts to their unique trading style.'),
    t('story.summary3', 'MiDash revolutionizes trading with AI that understands context, learns from your decisions, and connects seamlessly to your brokerage.')
  ];

  return (
    <SectionContainer id="story" className="bg-gradient-to-b from-black/20 to-transparent">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <h2 className="text-xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-8 text-center">
          <span className="text-white">{t('story.title')} </span>
          <span
            className="text-transparent bg-clip-text"
            style={{ backgroundImage: 'linear-gradient(135deg, #6dd8d0, #4db8b0)' }}
          >
            {t('story.titleHighlight')}
          </span>
        </h2>

        <div className={`space-y-3 md:space-y-4 ${isRTL ? 'text-right' : 'text-left'}`}>
          {/* Summary sentences */}
          {summaries.map((summary, idx) => (
            <div
              key={idx}
              className="rounded-xl px-4 py-3 md:px-5 md:py-4 border transition-all duration-300 hover:scale-[1.01]"
              style={{
                background: "rgba(34, 34, 34, 0.5)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                border: "1px solid rgba(91, 201, 193, 0.2)",
                boxShadow: "0 4px 16px rgba(91, 201, 193, 0.05)"
              }}
            >
              <p className="text-sm md:text-base leading-relaxed text-gray-200">
                {summary}
              </p>
            </div>
          ))}

          {/* Mission Statement */}
          <p className="text-sm md:text-lg lg:text-lg font-medium text-center mt-4 md:mt-6">
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: 'linear-gradient(135deg, #6dd8d0, #5bc9c1, #4db8b0)' }}
            >
              {t('story.mission')}
            </span>
          </p>

          {/* Learn More Button */}
          <div className="flex justify-center mt-6 md:mt-8">
            <button
              onClick={() => { trackClick("Learn More", "story-section"); navigate('/story'); }}
              className="group flex items-center gap-2 px-5 py-2.5 md:px-6 md:py-3 rounded-lg font-semibold text-sm md:text-base transition-all duration-300 hover:scale-105"
              style={{
                background: "linear-gradient(135deg, #6dd8d0, #4db8b0)",
                color: "#051b1f",
                boxShadow: "0 6px 20px rgba(91, 201, 193, 0.3)"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 8px 28px rgba(91, 201, 193, 0.45)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(91, 201, 193, 0.3)";
              }}
            >
              {t('common.learnMore', 'Learn More')}
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default StorySection;
